import {
  SearchApiModel,
  useAxios,
  useDeleteNulls,
  useAuthorization,
} from "@sttedra_ui/framework";

// Import the model for the response and request
import UsersApiResponseModel from "./users.api-response-model.js";
import UsersApiRequestModel from "./users.api-request-model.js";
import RolesApiResponseModel from "./roles.api-response-model.js";

// #region Globals
const { base } = useAxios();
const timeout = 5000;
let totalTries = 0;
// #endregion

// #region Public functions
function resetTotalTries() {
  totalTries = 0;
}

// Search users
async function appUserList(searchParams = {}) {
  try {
    // Create a new instance of the search model
    const searchModel = new SearchApiModel(searchParams);

    // Delete nulls from the search model
    useDeleteNulls(searchModel);

    const url = "/AppUser/list";

    const { data: response } = await base.post(url, searchModel, {
      timeout: 20000,
    });

    const { data: originalData, pageSize, pageIndex, totalResults } = response;

    const data = originalData.map((u) => new UsersApiResponseModel(u));

    return {
      data,
      pageSize,
      pageIndex,
      totalResults,
    };
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al buscar los registros");

    return err;
  }
}

// Get user details
async function usersGetDetails(email) {
  try {
    const url = `/AppUser/${email}/details`;

    // Make the request
    const response = await base.get(
      url,
      {},
      {
        timeout,
      }
    );

    return response;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener los detalles del usuario"
    );

    return err;
  }
}

// Get all user roles
async function getAllUserRoles() {
  try {
    const url = `/Users/GetAllRoles`;

    const response = await base.get(url, {
      timeout,
    });

    const data = response.data.map((u) => new RolesApiResponseModel(u));

    return data;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al buscar los registros");

    return err;
  }
}

// Update user rol
async function usersChangeRol(params = {}) {
  try {
    const processModel = new UsersApiRequestModel(params);

    useDeleteNulls(processModel);

    const url = `/Users/ChangeRol`;

    const response = await base.put(url, processModel, {
      timeout,
    });

    return response;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al actualizar el registro"
    );

    return err;
  }
}

// Get all users
async function appUserListAll(params = {}) {
  try {
    totalTries++;

    const url = `/AppUser/listAll`;

    // Make the request
    const response = await base.post(
      url,
      {
        email: params?.email,
        reportId: params?.reportId,
      },
      {
        timeout,
      }
    );

    let users = response.data;

    // Add a label for the impersonation dropdown
    users.forEach((user) => {
      user.label = user.nombre ? user.nombre : user.email;
      user.value = user.email;
    });

    const { modelRef: authorizationModelRef } = useAuthorization();
    const loggedUser = authorizationModelRef.value;

    if (params.fromImpersonation) {
      users = users.filter((user) => {
        return user.email !== loggedUser.email;
      });
    }

    return users;
  } catch (err) {
    if (totalTries < 3) {
      return appUserListAll(params);
    }

    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener los usuarios del sistema"
    );

    return [];
  }
}

// Update user role
async function appUserUpdateUserRol(params = {}) {
  try {
    const url = `/AppUser/updateUserRol`;

    // Make the request
    const response = await base.put(url, params, {
      timeout,
    });

    return response.data;
  } catch (err) {
    return err;
  }
}

// Create a internal user
async function appUserNewInternalUser(params = {}) {
  try {
    // Create a new instance of the request model
    const UsersModel = new UsersApiRequestModel(params);

    // Delete nulls from the request model
    useDeleteNulls(UsersModel);

    const url = "/AppUser/newInternalUser";

    const { data: response } = await base.post(url, UsersModel, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Create a external user
async function appUserNewExternalUser(params = {}) {
  try {
    // Create a new instance of the request model
    const UsersModel = new UsersApiRequestModel(params);

    // Delete nulls from the request model
    useDeleteNulls(UsersModel);

    const url = "/AppUser/newExternalUser";

    const { data: response } = await base.post(url, UsersModel, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Edit a user
async function appUserUpdateInternalExternalUser(params = {}) {
  try {
    // Create a new instance of the request model
    const UsersModel = new UsersApiRequestModel(params);

    // Delete nulls from the request model
    useDeleteNulls(UsersModel);

    const url = `/AppUser/UpdateInternalExternalUser`;

    // Make the request
    const { data: response } = await base.put(url, UsersModel, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Enable/disable a user
async function appUserEnableUser(email) {
  try {
    const url = `/AppUser/${email}/enableUser`;

    // Make the request
    const { data: response } = await base.put(url, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Delete a user
async function appUserDeleteUser(email) {
  try {
    const url = `/AppUser/${email}/deleteUser`;

    // Make the request
    const { data: response } = await base.delete(url, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Reset user password
async function appUserResetUserPassword(params = {}) {
  try {
    const url = `/AppUser/ResetUserPassword`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response.data;
  } catch (err) {
    return err;
  }
}

// User add group
async function userAddGroup(params = {}) {
  try {
    const url = `/AppUser/AddGroup`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response.data;
  } catch (err) {
    return err;
  }
}

// Get the users by group
async function appUserFindByGroup(params = {}) {
  try {
    const url = `/AppUser/findbygroup`;

    // Make the request
    const response = await base.post(url, params, {
      timeout: 20000,
    });

    return response;
  } catch (err) {
    return err;
  }
}
// #endregion

// #region Private functions
// #endregion

export {
  resetTotalTries,
  appUserList,
  usersGetDetails,
  getAllUserRoles,
  usersChangeRol,
  appUserListAll,
  appUserUpdateUserRol,
  appUserNewInternalUser,
  appUserNewExternalUser,
  appUserUpdateInternalExternalUser,
  appUserEnableUser,
  appUserDeleteUser,
  appUserResetUserPassword,
  userAddGroup,
  appUserFindByGroup,
};
